import { ref, computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import type { WorldeScoreDocument } from "@kekkle/shared/types/documents/wordle-score-document/wordle-score-document";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import { fetchGameIdPerDate } from "@/helpers/firestore/WordleGameService";
import { fetchGameScores } from "@/helpers/firestore/WorldeScoreService";
import {
  getBooleanFromLocalStorage,
  setLocalStorageItem,
} from "@/helpers/utils/local-storage";
import { useGroupsStore } from "@/stores/modules/groups/groups";

/**
 * Wordle scores store for managing daily scores
 * Uses Pinia with Composition API and TypeScript
 */
export const useWordleScoresStore = defineStore("wordleScores", () => {
  const groupsStore = useGroupsStore();

  /* State */
  const gameIds = ref<Record<string, string>>({});
  const onlyKnownUsers = ref<boolean>(
    getBooleanFromLocalStorage("onlyKnownUsersWorldeScores", true),
  );
  const scoreDate = ref<Date>(new Date());
  const scores = ref<Record<string, WorldeScoreDocument[]>>({});
  const fetchingScores = ref<Record<string, boolean>>({ "123": false });
  const fetchingGameId = ref<Record<string, boolean>>({ "01-01-2021": false });
  // Use shallowRef to prevent deep reactivity on Firestore snapshots
  const lastDocuments = shallowRef<
    Record<string, QueryDocumentSnapshot<DocumentData> | undefined>
  >({});
  const hasMoreData = ref<Record<string, boolean>>({});

  /* Computed / Getters */
  const currentGameId = computed<string | undefined>(() => {
    return gameIds.value[dateObjectToDateString(scoreDate.value, "dd-mm-yyyy")];
  });

  const currentScores = computed<WorldeScoreDocument[]>(() => {
    const gameId = currentGameId.value;
    if (!gameId || !scores.value[gameId]) {
      return [];
    }
    return scores.value[gameId];
  });

  const isFetchingScores = computed<boolean>(() => {
    return fetchingScores.value[currentGameId.value ?? ""] ?? false;
  });

  const isFetchingGameId = computed<boolean>(() => {
    return (
      fetchingGameId.value[
        dateObjectToDateString(scoreDate.value, "dd-mm-yyyy")
      ] ?? false
    );
  });

  const hasMore = computed<boolean>(() => {
    const gameId = currentGameId.value;
    return gameId ? (hasMoreData.value[gameId] ?? false) : false;
  });

  /* Actions */
  function setOnlyKnownUsersWordleScores(value: boolean): void {
    onlyKnownUsers.value = value;
    setLocalStorageItem("onlyKnownUsersWorldeScores", JSON.stringify(value));
  }

  function setScoreDate(date: Date): void {
    scoreDate.value = date;
  }

  function reset(): void {
    gameIds.value = {};
    scoreDate.value = new Date();
    scores.value = {};
    fetchingScores.value = {};
    fetchingGameId.value = {};
    lastDocuments.value = {};
    hasMoreData.value = {};
    onlyKnownUsers.value = true;
    setLocalStorageItem("onlyKnownUsersWorldeScores", "true");
  }

  async function fetchScores(date: Date, loadMore = false): Promise<void> {
    const dateString = dateObjectToDateString(date, "dd-mm-yyyy");
    let gameId: string | undefined = gameIds.value[dateString];

    if (!gameId) {
      fetchingGameId.value[dateString] = true;
      const fetchedGameId = await fetchGameIdPerDate(date);
      if (fetchedGameId) {
        gameId = fetchedGameId;
        gameIds.value[dateString] = gameId;
      }
      fetchingGameId.value[dateString] = false;
    }

    if (!gameId) return;

    fetchingScores.value[gameId] = true;

    let userFilter: string[] = [];
    if (onlyKnownUsers.value) {
      userFilter = groupsStore.allGroupMembersUuids;
    }

    const lastDoc = loadMore ? lastDocuments.value[gameId] : undefined;
    const result = await fetchGameScores(gameId, userFilter, {
      pageSize: 5,
      lastDoc,
    });

    result.docs.forEach((score) => {
      score.game_state = JSON.parse(score.game_state);
    });

    if (loadMore) {
      scores.value[gameId] = [...(scores.value[gameId] ?? []), ...result.docs];
    } else {
      scores.value[gameId] = result.docs;
    }

    // Store pagination state
    lastDocuments.value[gameId] = result.lastDoc;
    hasMoreData.value[gameId] = result.hasMore;

    fetchingScores.value[gameId] = false;
  }

  /* Exposed store */
  return {
    // State
    gameIds,
    onlyKnownUsers,
    scoreDate,
    scores,
    fetchingScores,
    fetchingGameId,
    lastDocuments,
    hasMoreData,

    // Computed
    currentGameId,
    currentScores,
    isFetchingScores,
    isFetchingGameId,
    hasMore,

    // Actions
    setOnlyKnownUsersWordleScores,
    setScoreDate,
    reset,
    fetchScores,
  };
});
